import StuartService from "../services/stuart-service";
import ClientService from "../services/client-service";
import ClientOps from "../services/client-ops-service";
import ShopService from "../services/shop-service";
import CartService from "../services/cart-service";
import DataStore from "../services/data-store";
import { base_url, CHECKOUT, ERROR_CODES, MSG_CLASS } from "../services/constants";
import CheckoutService from "./CheckoutService";
import OrderCalc from './order-calc-service'
import TicketService from "../services/ticket-service";
import growl from "../services/growl";
import _ from "lodash";
import Vue from 'vue';
import { Order } from "../../types";
let factory = Vue.observable({
	// from Payable.php
	error_codes: {
		CODE_UNKNOWN_TOKEN: 3040001,
		CODE_CARD_REFUSED: 1,
		CODE_FIDELITE_NOT_ENOUGH_POINTS: 2,
		CODE_COMO_PENDING_VERIFICATION_CODE: 4,
		CODE_COMO_INVALID_VERIFICATION_CODE: 8,
		CODE_CARD_NOT_INVALID: 500,
	},
	resetPaymentRequestAmounts,
	setConfirm,
	setCard,
	setEdenredPayment,
	setPaygreenPayment,
	getRemainFromFideliteCards,
	payUsingSelectedCard,
	payOrderAsync,
	getPayloadForPaying,
	confirmOrderWithoutPaying,
	getForwardedPayment,
	setFidelitePayment,
	needsCvc,
	needsComoCode,
	getMsgForCode,
	getModeFromOrder,
	needToPay,
	hasFidOrComoPayment,
});

export default factory;

function needToPay(order: Order) {
	return getRemainFromFideliteCards(order) != 0;
}

function getModeFromOrder(order: Order) {
	if (!needToPay(order)) {
		return 'confirm';
	}
	let pr = order.paymentRequest;
	if (pr.cardPayments?.length) {
		return 'cb';
	} else if (pr.confirmPayments?.length) {
		return 'confirm';
	} else if (pr.edenredPayments?.length) {
		return 'edenred';
	} else if (pr.paygreenPayments?.length) {
		return 'paygreen';
	}
	return null;
}

function getMsgForCode(code, defaultMsg) {
	switch (code) {
		case "4010202": return 'Numéro de carte invalide';
		case "4010206":
		case "4010207": return "Code CVC invalide";
	}
	return defaultMsg;
}

function needsCvc(error_code) {
	let codes = {
		CVC_Required: 4010206,
		CVC_Error: 4010207,
		CVC_Length_3_4: 400,
	};
	return Object.values(codes).find(v=>v==error_code);
}

function needsComoCode(error_code) {
	return [
		factory.error_codes.CODE_COMO_INVALID_VERIFICATION_CODE,
		factory.error_codes.CODE_COMO_PENDING_VERIFICATION_CODE,
	].includes(error_code);
}

function setFidelitePayment(order, fidCard) {
	let amount = _.floor(Math.min(fidCard.nbPoints, getRemainFromFideliteCards(order)),2);
	let fidPayment = {
		id: fidCard.id,
		GroupId: fidCard.GroupId,
		ShopId: fidCard.ShopId,
		cardno: fidCard.cardno,
		Amount: amount,
		method: ShopService.CONFIRM_ORDER_METHODS.BY_FIDELITE_CARD,
	};
	order.paymentRequest.fidelitePayments.splice(0, order.paymentRequest.fidelitePayments.length, fidPayment);
}

function getForwardedPayment(order) {
	return order.payments.find((payment) => payment.PaymentState == CartService.PAYMENT_STATES.PAYMENT_FORWARDING);
}

function resetPaymentRequestAmounts(order) {
	return OrderCalc.resetPaymentRequestAmounts(order);
}

function setCard(card, order) {
	// keep fidPayments and comoPayments, only alternate payments edenred/paygreen are cleared
	order.paymentRequest.edenredPayments = [];
	order.paymentRequest.paygreenPayments = [];
	order.paymentRequest.confirmPayments = [];
	if (!card) {
		order.paymentRequest.cardPayments = [];
		return;
	}
	let remain = getRemainFromFideliteCards(order);
	if (remain) {
		let cardPayment = {
			CCid: card.CCid,
			Amount: remain,
			method: ShopService.CONFIRM_ORDER_METHODS.BY_CREDIT_CARD,
			browser_info: ClientOps.getBrowserInfo(),
		};
		order.paymentRequest.cardPayments = [cardPayment];
		if (!order.OrderInfo) {
			order.OrderInfo = "Nom: " + card.card_holder;
		}
	}
}

function setConfirm(order: Order) {
	setCard(null ,order)
	order.paymentRequest.confirmPayments = [ { method: ShopService.CONFIRM_ORDER_METHODS.CONFIRM_ONLY, Amount: 0 }];
}

function hasFidOrComoPayment(order: Order) {
	return order.paymentRequest.comoPayments.length || order.paymentRequest.fidelitePayments.length;
}

function setPaygreenPayment(order: Order, paymentType: string) {
	let remain = getRemainFromFideliteCards(order);
	let payment = {
		Amount: remain,
		paymentType: paymentType,
	}
	order.paymentRequest.cardPayments = [];
	order.paymentRequest.confirmPayments = [];
	order.paymentRequest.edenredPayments = [];
	order.paymentRequest.paygreenPayments = [payment];
	// cannot reuse OrderId, because we cant make a second payment request for the same OrderId (paygreen returns an error)
	delete order.OrderId;
}

function setEdenredPayment(order: Order, amount: number) {
	let remain = getRemainFromFideliteCards(order);
	let remain_after_payment = remain - amount;
	let edenredPayment = {
		Amount: amount,
		method: ShopService.CONFIRM_ORDER_METHODS.BY_EDENRED,
		authorize: Math.abs(remain_after_payment) > 0.01,
	};
	order.paymentRequest.edenredPayments = [edenredPayment];
	order.paymentRequest.cardPayments = [];
	order.paymentRequest.confirmPayments = [];
}

function getRemainFromFideliteCards(order) {
	let remain = CheckoutService.isTicket(order) ? order.PaymentAmount : order.Remain;
	if (!_.round(remain,2)) return 0;
	return _.round(remain
		- _.sumBy(order.paymentRequest.fidelitePayments, 'Amount')
		- _.sumBy(order.paymentRequest.comoPayments, 'Amount')
	,2);
}

function payUsingSelectedCard2(order: Order, firstResponse: (data: {}) => void) {
	if (CheckoutService.isTicket(order)) {
		return payTicketAsync(order, firstResponse);
	} else {
		return payOrderAsync(order, firstResponse);
	}
}

function payUsingSelectedCard({ card = null, cardRefused = null, error_code = null, cvc = null, order },
	msgCallback: (msg: string, msgClass: typeof MSG_CLASS[keyof typeof MSG_CLASS], focusCvc?: boolean) => void,
	) {
	// updateCardAsync must use a different error handling from regular payment; only on success
	let promise = Promise.resolve();

	let updateCvc = card?.CCid == cardRefused?.CCid && needsCvc(error_code);
	if (updateCvc && !cvc) {
		msgCallback("Code de sécurité obligatoire", MSG_CLASS.DANGER, true);
		return;
	}

	let firstResponse;
	if (updateCvc) {
		msgCallback("Vérification du code de sécurité ...",MSG_CLASS.INFO);
		firstResponse = (data) => {
			if (data.OrderId) {
				msgCallback('Code vérifié, paiement en cours ...',MSG_CLASS.INFO);
				order.OrderId = data.OrderId;
			}
		};
		promise = ClientOps.updateCardAsync(card, order.ClientId, cvc)
	} else {
		// dont infoCallback() here; the first message is set before calling this method
		// because, there a different message for new card or exisintg card
		firstResponse = (data) => {
			if (data.OrderId) {
				msgCallback('Commande enregistrée, paiement en cours ...',MSG_CLASS.INFO)
				order.OrderId = data.OrderId;
			}
		};
	}
	return promise
	.then(() => payUsingSelectedCard2(order, firstResponse))
	.catch((err) => {
		let error_code;
		let msg;
		if (_.isError(err)) {
			// error 500 after first response
			msg = 'Erreur lors du paiement';
			error_code = null;
			console.error(err);
		} else {
			msg = getMsgForCode(err.code, err.msg);
			error_code = err.code;
		}
		if (needsCvc(error_code)) {
			growl.warning("Entrez le code CVC à 3 chiffres au dos votre carte");
			msgCallback(msg, MSG_CLASS.DANGER, true);
		} else if (error_code == factory.error_codes.CODE_COMO_PENDING_VERIFICATION_CODE) {
			this.setMsg("Entrez le code de vérification, puis confirmez à nouveau", MSG_CLASS.INFO);
		} else if (error_code == factory.error_codes.CODE_COMO_INVALID_VERIFICATION_CODE) {
			this.setMsg("Code de vérification invalide", MSG_CLASS.DANGER);
		} else {
			msgCallback(msg, MSG_CLASS.DANGER);
		}
		return Promise.reject({
			cardRefused: card,
			error_code,
		});
	});
}


let lastTotal;
function getPayloadForPaying(order) {
	let payload;
	let totalChanged = order.Total != lastTotal;
	lastTotal = order.Total;
	if (order.OrderId && !totalChanged) {
		payload = {
			order: {
				OrderId: order.OrderId,
				ShopId: order.ShopId,
			},
			paymentRequest: order.paymentRequest,
		};
	} else {
		let json_order = CartService.orderToJson(order);
		payload = {
			update_order: true,
			order: json_order,
			ShopId: order.ShopId,
			paymentRequest: order.paymentRequest,
		};
	}
	return payload;
}

function confirmOrderWithoutPaying(order) {
	let url = base_url + "/admin/orders/confirm-order?ClientId=" + order.ClientId;
	let payload = { order: CartService.orderToJson(order) };
	return DataStore.post(url, payload, "")
	.then(data => data.payable)
}

function payOrderAsync(order, firstResponse) {
		order.OrderState = CartService.ORDER_STATES.STATE_VALIDATED_UNCONFIRMED;
		let payload = getPayloadForPaying(order);
		return DataStore.post2Responses(base_url + "/admin/orders/pay-order?ClientId=" + order.ClientId, payload, firstResponse)
			.then(data => {
				if (CheckoutService.isOrderConfirmed(data.payable)) {
					CheckoutService.confirmOrder(data.payable);
					growl.success("Paiement effectué");
				} else {
					// not confirmed yet; pending or forwarding
				}
				return data;
			})
}

// need to refactor into u sing only payOrderAsync2()
function payTicketAsync(ticket, firstResponse) {
	// let sumByAmount = collection => _.sumBy(collection, "Amount");
	// ned to ckeck
	// let payingAmount =
	// 	sumByAmount(ticket.paymentRequest.fidelitePayments) + sumByAmount(ticket.paymentRequest.cardPayments);
	// if (payingAmount < ticket.PaymentAmount && Math.abs(payingAmount - ticket.PaymentAmount) > 0.01) {
	// 	let msg = "Choisissez une carte de paiement";
	// 	growl.error(msg);
	// 	return;
	// }
	let payload = {
		paymentRequest: ticket.paymentRequest,
		ticket: _.pick(ticket, ["DateService", "id", "Ntik", "ShopId", "Table", "TicketId", "Total"]),
	};
	ticket.paymentSuccess = false;
	return DataStore.post2Responses(base_url + "/admin/ticket/pay-ticket", payload, firstResponse)
		.then(data => {
			ticket.paymentSuccess = true;
			ticket = TicketService.mergePayable(ticket, data.payable);
			if (data.payment.PaymentState == "PAYMENT_FORWARDING") {
				// nothing to do yet
			} else {
				growl.success("Paiement effectué");
			}
			data.payable = ticket;
			return data;
		})
}